import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CountUp from "react-countup";
import axios from "axios";
import { Pencil, Trash2, Mail, Link2 } from "lucide-react";
import { formatCurrency } from "../lib/formatCurrency";
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { DisplayOptionsBar, DEFAULT_DISPLAY_OPTIONS, loadRememberedDocSettings, rememberDocSettings, recalcItems, lineTotal, symbolFor } from '../components/docDisplayOptions';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Check, ChevronsUpDown, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { getInvoices, createInvoice, getPayments, createPayment, getDashboardFinance, getInvoicePDF } from '../lib/api';
import { Plus, Search, DollarSign, Receipt, CreditCard, TrendingUp, AlertCircle, CheckCircle, Download, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ['#10B981', '#D97706', '#E11D48', '#6366F1'];
const PAYMENT_METHODS = ['eft', 'card', 'cash', 'payfast', 'other'];

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  pending: 'bg-amber-100 text-amber-700',
  partial: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-rose-100 text-rose-700'
};



// Invoice Form Component
const InvoiceForm = ({ invoice, clients = [], onSave, onClose }) => {
  const DEFAULT_TERMS = `• All prices are in Rands.
• The contract price is stated in the quotation and it may be varied in accordance with conditions.
• Late payment entitles the supplier to interest at statutory rate of 10%.
• Cancellation of quotation after being accepted may result in 25% penalty.
• Payment: Payment can be made by electronic transfer, and must be received prior to training dates. Please quote the reference number from your invoice so that payment can be tracked. Your reservation will be cancelled should payment not be received prior to the training date unless a purchase order is provided.
• Cancellation and Substitutions: All cancellations must be done in writing and emailed directly to IKWORX. Less than five (5) working days = 100% of the course fee. Substitutions can be made at any time without incurring a penalty.
• Transfers & Postponements: Transfers can only be made up to five (5) working days prior to the course date. The option to transfer can only be used once within (3) three months.`;

  const remembered = loadRememberedDocSettings();
  const [form, setForm] = useState(
    invoice
      ? {
          ...invoice,
          items: (invoice.items && invoice.items.length > 0
            ? invoice.items
            : [{ description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }]
          ).map((it) => ({
            description: it.description || '',
            quantity: it.quantity ?? 1,
            incl_price: it.incl_price ?? it.unit_price ?? 0,
            disc_pct: it.disc_pct ?? 0,
            disc_amount: it.disc_amount ?? 0,
            vat_pct: it.vat_pct ?? it.vat_rate ?? 15,
          })),
          bank_details: invoice.bank_details || {
            bank: 'NedBank',
            account_name: 'Innovative Knowledge Worx Trading and Projects (PTY)LTD',
            account_number: '1274012465',
            branch: 'RRB MELROSE ARCH',
            branch_code: '198765',
          },
          terms_and_conditions: invoice.terms_and_conditions || DEFAULT_TERMS,
          overall_discount_pct: invoice.overall_discount_pct ?? 0,
          reference: invoice.reference || '',
          sales_rep: invoice.sales_rep || '',
          client_vat_no: invoice.client_vat_no || '',
          client_postal_address: invoice.client_postal_address || '',
          client_physical_address: invoice.client_physical_address || '',
          display_options: invoice.display_options || { ...DEFAULT_DISPLAY_OPTIONS },
          currency: invoice.currency || 'ZAR',
        }
      : {
          client_name: '',
          client_email: '',
          client_vat_no: '',
          client_postal_address: '',
          client_physical_address: '',
          reference: '',
          sales_rep: '',
          overall_discount_pct: 0,
          items: [{ description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }],
          subtotal: 0,
          vat_rate: 15,
          vat_amount: 0,
          total: 0,
          currency: remembered?.currency || 'ZAR',
          display_options: remembered?.display_options || { ...DEFAULT_DISPLAY_OPTIONS },
          status: 'pending',
          invoice_date: new Date().toISOString().split('T')[0],
          valid_until: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          valid_until_overridden: false,
          due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          notes: '',
          bank_details: {
            bank: 'NedBank',
            account_name: 'Innovative Knowledge Worx Trading and Projects (PTY)LTD',
            account_number: '1274012465',
            branch: 'RRB MELROSE ARCH',
            branch_code: '198765',
          },
          terms_and_conditions: DEFAULT_TERMS,
        }
  );
  const [saving, setSaving] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [useManualClient, setUseManualClient] = useState(false);

  const applyClient = (c) => {
    if (!c) return;
    setForm({
      ...form,
      client_id: c.id,
      client_name: c.company || '',
      client_email: c.contact_email || '',
      client_vat_no: c.vat_number || '',
      client_postal_address: c.billing_address || form.client_postal_address || '',
      client_physical_address: c.billing_address || form.client_physical_address || '',
    });
    setClientPickerOpen(false);
    setUseManualClient(false);
    toast.success(`Loaded ${c.company}'s details`);
  };

  // Calculate totals from line items, respecting display options
  const recalc = (items, opts = form.display_options) => recalcItems(items, opts || DEFAULT_DISPLAY_OPTIONS);

  const setDisplayOptions = (opts) => setForm({ ...form, display_options: opts, ...recalcItems(form.items, opts) });
  const setCurrency = (cur) => setForm({ ...form, currency: cur });
  const opts = form.display_options || DEFAULT_DISPLAY_OPTIONS;
  const curSym = symbolFor(form.currency || 'ZAR');
  const fmtMoney = (v) => `${curSym}${(Number(v) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems, ...recalc(newItems) });
  };

  const addItem = () => {
    const newItems = [...form.items, { description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }];
    setForm({ ...form, items: newItems, ...recalc(newItems) });
  };

  const removeItem = (idx) => {
    const remaining = form.items.filter((_, i) => i !== idx);
    const newItems = remaining.length ? remaining : [{ description: '', quantity: 1, incl_price: 0, disc_pct: 0, disc_amount: 0, vat_pct: 15 }];
    setForm({ ...form, items: newItems, ...recalc(newItems) });
  };

  const setBank = (field, value) => setForm({ ...form, bank_details: { ...(form.bank_details || {}), [field]: value } });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name) {
      toast.error('Please select or enter a client name');
      return;
    }
    setSaving(true);
    try {
      if (invoice) {
        await axios.put(`${API}/invoices/${invoice.id}`, form);
        toast.success('Invoice updated');
      } else {
        await createInvoice(form);
        toast.success('Invoice created');
      }
      rememberDocSettings(form.display_options, form.currency);
      onSave();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[78vh] overflow-y-auto pr-2">
      {/* Top meta */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label>Sales Rep</Label>
          <Input value={form.sales_rep || ''} onChange={(e) => setForm({ ...form, sales_rep: e.target.value })} placeholder="e.g. Fulufhelo Matodzi" data-testid="invoice-sales-rep" />
        </div>
        <div className="space-y-1">
          <Label>Invoice Date</Label>
          <Input type="date" value={form.invoice_date || ''} data-testid="invoice-date-input"
            onChange={(e) => {
              const d = e.target.value;
              const next = { ...form, invoice_date: d };
              if (d && !form.valid_until_overridden) {
                next.valid_until = new Date(new Date(d).getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
              }
              setForm(next);
            }} />
        </div>
        <div className="space-y-1">
          <Label>Due Date</Label>
          <Input type="date" value={form.due_date || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label>Valid Until <span className="text-[10px] text-muted-foreground">(default: invoice date + 90 days)</span></Label>
          <Input type="date" value={form.valid_until || ''} data-testid="invoice-valid-until-input"
            onChange={(e) => setForm({ ...form, valid_until: e.target.value, valid_until_overridden: true })} />
        </div>
        <div className="space-y-1">
          <Label>Overall Discount %</Label>
          <Input type="number" min="0" max="100" step="0.01" value={form.overall_discount_pct ?? 0} onChange={(e) => setForm({ ...form, overall_discount_pct: parseFloat(e.target.value) || 0 })} data-testid="invoice-overall-discount" />
        </div>
      </div>
      <div className="space-y-1">
        <Label>Reference</Label>
        <Input value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. Course / Unit Standard / PO number" data-testid="invoice-reference" />
      </div>

      {/* Display options + currency */}
      <DisplayOptionsBar options={opts} onChange={setDisplayOptions} currency={form.currency || 'ZAR'} onCurrencyChange={setCurrency} idPrefix="invoice" />

      {/* Client (TO) block */}
      <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
        <p className="text-sm font-semibold">Bill To (Customer)</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Client Name *</Label>
            {useManualClient ? (
              <div className="flex gap-2">
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required autoFocus data-testid="invoice-client-input" />
                <Button type="button" variant="outline" onClick={() => setUseManualClient(false)} title="Pick from client list" data-testid="invoice-client-use-picker">
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="invoice-client-picker">
                    <span className={form.client_name ? '' : 'text-slate-400'}>{form.client_name || 'Select a client...'}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients..." data-testid="invoice-client-search" />
                    <CommandList>
                      <CommandEmpty>No clients found.</CommandEmpty>
                      <CommandGroup heading="Clients">
                        {clients.map((c) => (
                          <CommandItem key={c.id} value={c.company || c.id} onSelect={() => applyClient(c)} data-testid={`invoice-client-option-${c.id}`}>
                            <Check className={`mr-2 h-4 w-4 ${form.client_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="min-w-0">
                              <p className="text-sm truncate">{c.company}</p>
                              {c.contact_email && <p className="text-xs text-slate-500 truncate">{c.contact_email}</p>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup>
                        <CommandItem onSelect={() => { setUseManualClient(true); setClientPickerOpen(false); }} data-testid="invoice-client-manual">
                          <UserPlus className="mr-2 h-4 w-4" /> Enter client manually
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <div className="space-y-1">
            <Label>Customer VAT No</Label>
            <Input value={form.client_vat_no || ''} onChange={(e) => setForm({ ...form, client_vat_no: e.target.value })} data-testid="invoice-client-vat" />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Client Email</Label>
          <Input type="email" value={form.client_email || ''} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Postal Address</Label>
            <Textarea rows={3} value={form.client_postal_address || ''} onChange={(e) => setForm({ ...form, client_postal_address: e.target.value })} placeholder="Street&#10;Suburb&#10;City&#10;Postal Code" data-testid="invoice-client-postal" />
          </div>
          <div className="space-y-1">
            <Label>Physical Address</Label>
            <Textarea rows={3} value={form.client_physical_address || ''} onChange={(e) => setForm({ ...form, client_physical_address: e.target.value })} placeholder="Street&#10;Suburb&#10;City&#10;Postal Code" data-testid="invoice-client-physical" />
          </div>
        </div>
      </div>

      {/* Line items table */}
      <div className="space-y-2">
        <Label>Line Items</Label>
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-1 bg-slate-100 px-2 py-1.5 text-xs font-semibold">
            <div className="col-span-4">Description</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">{opts.include_tax_in_price !== false ? 'Incl. Price' : 'Price (Excl.)'}</div>
            {!opts.hide_discount && <div className="col-span-1 text-right">{opts.percentage_discounts !== false ? 'Disc %' : `Disc (${curSym.trim()})`}</div>}
            {!opts.hide_tax && <div className="col-span-1 text-right">VAT %</div>}
            <div className="col-span-2 text-right">Total</div>
          </div>
          <div className="divide-y">
            {form.items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center">
                <div className="col-span-4">
                  <Input className="h-8" placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} data-testid={`item-desc-${idx}`} />
                </div>
                <Input className="col-span-1 h-8 text-right" type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} data-testid={`item-qty-${idx}`} />
                <Input className="col-span-2 h-8 text-right" type="number" min="0" step="0.01" value={item.incl_price} onChange={(e) => updateItem(idx, 'incl_price', parseFloat(e.target.value) || 0)} data-testid={`item-incl-price-${idx}`} />
                {!opts.hide_discount && (opts.percentage_discounts !== false ? (
                  <Input className="col-span-1 h-8 text-right" type="number" min="0" max="100" step="0.01" value={item.disc_pct} onChange={(e) => updateItem(idx, 'disc_pct', parseFloat(e.target.value) || 0)} />
                ) : (
                  <Input className="col-span-1 h-8 text-right" type="number" min="0" step="0.01" value={item.disc_amount} onChange={(e) => updateItem(idx, 'disc_amount', parseFloat(e.target.value) || 0)} />
                ))}
                {!opts.hide_tax && (
                  <Input className="col-span-1 h-8 text-right" type="number" min="0" max="100" step="0.01" value={item.vat_pct} onChange={(e) => updateItem(idx, 'vat_pct', parseFloat(e.target.value) || 0)} />
                )}
                <div className="col-span-2 text-right text-sm font-mono">{fmtMoney(lineTotal(item, opts))}</div>
                <button type="button" className="col-span-1 text-red-500 hover:text-red-700 text-xs" onClick={() => removeItem(idx)} title="Remove line">✕ Remove</button>
              </div>
            ))}
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>+ Add Line</Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-4 text-right pt-2 border-t">
        {!opts.hide_exclusive_total && (<>
          <div></div>
          <div className="text-sm text-muted-foreground">Total Exclusive:</div>
          <div className="font-mono" data-testid="invoice-subtotal">{fmtMoney(form.subtotal)}</div>
        </>)}
        {!opts.hide_tax && (<>
          <div></div>
          <div className="text-sm text-muted-foreground">Total VAT:</div>
          <div className="font-mono" data-testid="invoice-vat-total">{fmtMoney(form.vat_amount)}</div>
        </>)}
        <div></div>
        <div className="font-semibold">Grand Total:</div>
        <div className="font-mono font-bold text-lg" data-testid="invoice-grand-total">{fmtMoney(form.total)}</div>
      </div>

      {/* Bank details (with sensible defaults) */}
      <details className="border rounded-lg p-3" open>
        <summary className="text-sm font-semibold cursor-pointer">Bank Details (shown on PDF)</summary>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="space-y-1"><Label>Bank</Label><Input value={form.bank_details?.bank || ''} onChange={(e) => setBank('bank', e.target.value)} /></div>
          <div className="space-y-1"><Label>Account Name</Label><Input value={form.bank_details?.account_name || ''} onChange={(e) => setBank('account_name', e.target.value)} /></div>
          <div className="space-y-1"><Label>Account Number</Label><Input value={form.bank_details?.account_number || ''} onChange={(e) => setBank('account_number', e.target.value)} /></div>
          <div className="space-y-1"><Label>Branch</Label><Input value={form.bank_details?.branch || ''} onChange={(e) => setBank('branch', e.target.value)} /></div>
          <div className="space-y-1"><Label>Branch Code</Label><Input value={form.bank_details?.branch_code || ''} onChange={(e) => setBank('branch_code', e.target.value)} /></div>
        </div>
      </details>

      {/* Terms */}
      <details className="border rounded-lg p-3">
        <summary className="text-sm font-semibold cursor-pointer">Terms &amp; Conditions (shown on PDF)</summary>
        <Textarea className="mt-3" rows={6} value={form.terms_and_conditions || ''} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })} data-testid="invoice-terms" />
      </details>

      {/* Notes */}
      <div className="space-y-1">
        <Label>Notes (optional)</Label>
        <Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t bg-background sticky bottom-0">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-invoice-btn">
          {saving ? 'Saving...' : invoice ? 'Update Invoice' : 'Create Invoice'}
        </Button>
      </div>
    </form>
  );
};

// Payment Form Component
const PaymentForm = ({ invoices, onSave, onClose }) => {
  const [form, setForm] = useState({
    invoice_id: '', amount: 0, method: 'eft', reference: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const selectedInvoice = invoices.find(i => i.id === form.invoice_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createPayment(form);
      toast.success('Payment recorded');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Invoice *</Label>
        <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
          <SelectTrigger data-testid="payment-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
          <SelectContent>
            {invoices.filter(i => i.balance > 0).map(inv => (
              <SelectItem key={inv.id} value={inv.id}>
                {inv.invoice_number} - {inv.client_name} (Balance: {formatCurrency(inv.balance)})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {selectedInvoice && (
        <div className="p-4 bg-muted rounded-lg">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total</p>
              <p className="font-mono font-semibold">{formatCurrency(selectedInvoice.total)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Paid</p>
              <p className="font-mono font-semibold text-emerald-600">{formatCurrency(selectedInvoice.amount_paid)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Balance</p>
              <p className="font-mono font-semibold text-amber-600">{formatCurrency(selectedInvoice.balance)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Amount *</Label>
          <Input type="number" min="0" max={selectedInvoice?.balance || 0} value={form.amount} 
            onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} 
            required data-testid="payment-amount-input" />
        </div>
        <div className="space-y-2">
          <Label>Payment Method</Label>
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map(m => (
                <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Reference</Label>
        <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Transaction reference or receipt number" />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving || !form.invoice_id || form.amount <= 0} data-testid="save-payment-btn">
          {saving ? 'Processing...' : 'Record Payment'}
        </Button>
      </div>
    </form>
  );
};

// Main Finance Page Component
export const FinancePage = () => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [clients, setClients] = useState([]);
const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    loadData();
    // Load clients for the invoice client picker
    axios.get(`${API}/clients`).then(res => setClients(res.data || [])).catch(() => setClients([]));
  }, []);

  const loadData = async () => {
    try {
      // Load data independently so one failure doesn't block others
      const [invRes, payRes, dashRes] = await Promise.allSettled([
        getInvoices(), getPayments(), getDashboardFinance()
      ]);
      
      if (invRes.status === 'fulfilled') {
        setInvoices(invRes.value.data);
      } else {
        console.error('Failed to load invoices:', invRes.reason);
      }
      
      if (payRes.status === 'fulfilled') {
        setPayments(payRes.value.data);
      } else {
        console.error('Failed to load payments:', payRes.reason);
      }
      
      if (dashRes.status === 'fulfilled') {
        setDashboard(dashRes.value.data);
      } else {
        console.error('Failed to load dashboard:', dashRes.reason);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

const deletePayment = async (id) => {
  console.log("Deleting receipt id:", id);

  if (!window.confirm("Delete this payment?")) return;

  try {
    await axios.delete(`${API}/customer-receipts/${id}`);
    toast.success("Payment deleted");
    loadData();
  } catch (error) {
    console.error("Delete payment error:", error);
    toast.error("Failed to delete payment");
  }
};

const handleEditInvoice = (invoice) => {
  console.log("Editing invoice:", invoice);

  setFormData(invoice);        // fill form with invoice data
  setInvoiceDialogOpen(true);  // open invoice dialog
};

 const handleDeleteInvoice = async (id) => {
  if (!window.confirm("Delete this invoice?")) return;

  try {
    await axios.delete(`${API}/invoices/${id}`);
    toast.success("Invoice deleted");
    loadData();
  } catch (error) {
    console.error("Delete invoice error:", error);
    toast.error("Failed to delete invoice");
  }
};

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const agingData = dashboard?.aging_analysis ?
    Object.entries(dashboard.aging_analysis).map(([name, value]) => ({
      name: name + ' days',
      value
    })) : [];

    console.log("Payments data:", payments);
  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }


  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Financial Management</h1>
          <p className="text-muted-foreground mt-1">Invoices, payments, and financial overview</p>
        </div>
        <Button
          size="lg"
          onClick={() => { setFormData(null); setInvoiceDialogOpen(true); }}
          data-testid="header-create-invoice-btn"
        >
          <Receipt className="h-4 w-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <Receipt className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-2xl font-bold font-mono">
  <CountUp
    end={dashboard?.total_invoiced || 0}
    duration={1.5}
    formattingFn={formatCurrency}
  />
</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collected</p>
                <p className="text-2xl font-bold font-mono text-emerald-600"><p className="text-2xl font-bold font-mono text-emerald-600">
  <CountUp
    end={dashboard?.total_collected || 0}
    duration={1.5}
    formattingFn={formatCurrency}
  />
</p></p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold font-mono text-amber-600"><p className="text-2xl font-bold font-mono text-amber-600">
  <CountUp
    end={dashboard?.total_outstanding || 0}
    duration={1.5}
    formattingFn={formatCurrency}
  />
</p></p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card relative overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collection Rate</p>
                <p className="text-2xl font-bold font-mono"><p className="text-2xl font-bold font-mono">
  <CountUp
    end={dashboard?.collection_rate || 0}
    duration={1.2}
    decimals={1}
    suffix="%"
  />
</p></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Receivables Aging Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {agingData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Dialog open={invoiceDialogOpen} onOpenChange={(open) => { setInvoiceDialogOpen(open); if (!open) setFormData(null); }}>
              <DialogTrigger asChild>
                <Button className="w-full justify-start" variant="outline" data-testid="create-invoice-btn" onClick={() => setFormData(null)}>
                  <Receipt className="h-4 w-4 mr-2" /> Create Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl">
  <DialogHeader>
    <DialogTitle>
      {formData?.id ? "Edit Invoice" : "Create Invoice"}
    </DialogTitle>
  </DialogHeader>

  <InvoiceForm
    invoice={formData}
    clients={clients}
    onSave={loadData}
    onClose={() => setInvoiceDialogOpen(false)}
  />
</DialogContent>
            </Dialog>

            <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full justify-start" variant="outline" data-testid="record-payment-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Record Payment</DialogTitle>
                </DialogHeader>
                <PaymentForm invoices={invoices} onSave={loadData} onClose={() => setPaymentDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Tabs defaultValue="invoices" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="invoices-tab">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab">Payments ({payments.length})</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." className="pl-9 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Button onClick={() => { setFormData(null); setInvoiceDialogOpen(true); }} data-testid="tab-create-invoice-btn">
              <Receipt className="h-4 w-4 mr-2" /> New Invoice
            </Button>
          </div>
        </div>

        <TabsContent value="invoices">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                    <TableCell className="font-medium">{inv.client_name}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(inv.total)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(inv.amount_paid)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-600">{formatCurrency(inv.balance)}</TableCell>
                    <TableCell className="text-sm">{inv.invoice_date?.split('T')[0] || inv.created_at?.split('T')[0] || '-'}</TableCell>
                    <TableCell className="text-sm">{inv.due_date?.split('T')[0] || '-'}</TableCell>
                    <TableCell className="text-sm" data-testid={`invoice-valid-until-${inv.id}`}>{inv.valid_until?.split('T')[0] || '-'}</TableCell>
                    <TableCell><Badge className={statusColors[inv.status]}>{inv.status}</Badge></TableCell>
                    <TableCell className="text-right">
  <div className="flex items-center justify-end gap-2">

    {/* Payment link */}
    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
      <Button
        variant="ghost"
        size="icon"
        onClick={async () => {
          try {
            const res = await axios.post(`${API}/invoices/${inv.id}/pay-link`);
            await navigator.clipboard.writeText(res.data.url);
            toast.success('Payment link copied — send it to your client');
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not create payment link');
          }
        }}
        title="Copy PayFast payment link"
        data-testid={`invoice-pay-link-${inv.id}`}
      >
        <Link2 className="h-4 w-4 text-emerald-600" />
      </Button>
    )}

    {/* Edit */}
    <Button
      variant="ghost"
      size="icon"
      onClick={() => handleEditInvoice(inv)}
      title="Edit Invoice"
    >
      <Pencil className="h-4 w-4 text-blue-600" />
    </Button>

    {/* Delete */}
    <Button
      variant="ghost"
      size="icon"
      onClick={() => handleDeleteInvoice(inv.id)}
      title="Delete Invoice"
    >
      <Trash2 className="h-4 w-4 text-red-600" />
    </Button>

    {/* Email to Client */}
    <Button
      variant="ghost"
      size="icon"
      onClick={async (e) => {
        e.stopPropagation();
        if (!inv.client_email) {
          toast.error('No client email on this invoice — edit it and add one first');
          return;
        }
        if (!window.confirm(`Email invoice ${inv.invoice_number} to ${inv.client_email}? It will be sent from your email address.`)) return;
        try {
          const res = await axios.post(`${API}/invoices/${inv.id}/email`, {});
          toast.success(res.data.message);
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Failed to email invoice');
        }
      }}
      title="Email to Client"
      data-testid={`email-invoice-${inv.id}`}
    >
      <Mail className="h-4 w-4 text-emerald-600" />
    </Button>

    {/* Download */}
    <Button
      variant="ghost"
      size="icon"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          const response = await getInvoicePDF(inv.id);
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `invoice_${inv.invoice_number}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success('Invoice PDF downloaded');
        } catch {
          toast.error('Failed to download PDF');
        }
      }}
      title="Download PDF"
    >
      <Download className="h-4 w-4" />
    </Button>

  </div>
</TableCell>
                  </TableRow>
                ))}
                {filteredInvoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invoices found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => {
                  const invoice = invoices.find(i => i.id === payment.invoice_id);
                  return (
                    <TableRow key={payment.id}>
                      <TableCell className="text-sm">{payment.created_at?.split('T')[0]}</TableCell>
                      <TableCell className="font-mono text-sm">{invoice?.invoice_number || '-'}</TableCell>
                      <TableCell className="uppercase text-sm">{payment.method}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-600">{formatCurrency(payment.amount)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{payment.reference || '-'}</TableCell>
                       <TableCell className="text-right">
    <Button
  variant="destructive"
  size="sm"
  onClick={() => deletePayment(payment.id)}
>
  Delete
</Button>
    </TableCell>
                    </TableRow>
                  );
                })}
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No payments recorded</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
